import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../../components/SEOMeta";
import styles from "../../../styles/Button.module.css";

const WebApplicationAttacks: React.FC = () => {
  // List of web application attacks
  const attacks = [
    "Buffer Overflow",
    "Clickjacking (UI Redress)",
    "Command Injection",
    "Cross-Site Request Forgery (CSRF/XSRF)",
    "Cross-Site Scripting (XSS)",
    "Directory Traversal/Path Traversal",
    "File Inclusion (LFI/RFI)",
    "Formjacking",
    "HTTP Flood",
    "Insecure Direct Object Reference (IDOR)",
    "LDAP Injection",
    "Remote Code Execution (RCE)",
    "Server-Side Request Forgery (SSRF)",
    "SQL Injection (SQLi)",
    "XML External Entity (XXE) Injection",
    "XPath Injection",
    "Template Injection",
    "Broken Access Control Exploitation",
    "Security Misconfiguration Exploitation",
    "Software Supply Chain Compromise",
    "JavaScript Injection at Scale",
    "API Abuse/Overloading",
    "HTTP/2 Rapid Reset",
    "WebSocket Hijacking",
    "gRPC Abuse",
    "Serverless Function RCE",
    "Mass Assignment Exploitation",
    "JWT Token Misconfiguration",
    "GraphQL Batching & Nested Query Attacks",
    "OAuth 2.0 Device Flow Phishing",
    "Cloud Metadata Service Exploitation (IMDSv1)",
    "DOM Clobbering",
    "Web Cache Poisoning via Unkeyed Inputs",
    "Prototype Pollution (JavaScript)",
    "Deserialization Attacks (Insecure Deserialization)"
  ];

  const [searchTerm, setSearchTerm] = useState("");

  // Filter attacks based on search term
  const filteredAttacks = attacks.filter(attack =>
    attack.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Web & Application Cyber Attacks: Complete Guide to Web Security Threats in 2026",
    description:
      "Complete guide to web and application cyber attacks, including XSS, CSRF, SQL injection, and other web application vulnerabilities. Learn about prevention and defense strategies.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/category/web-application",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="Web & Application Cyber Attacks: Complete Guide"
        description="Complete guide to web and application cyber attacks, including XSS, CSRF, SQL injection, and other web application vulnerabilities. Learn about prevention and defense strategies."
        keywords="web attacks, application attacks, XSS, CSRF, SQL injection, web security, application vulnerabilities, OWASP, injection attacks, cross-site scripting, request forgery, web application firewall"
        canonical="https://cyberannals.com/blog/category/web-application"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            Web & Application Cyber Attacks
          </h1>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            Complete Guide to Web Security Threats in 2026
          </h2>
        </header>

        <div className="space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
          <p className="text-lg">
            This page lists all web and application cyber attacks. Each attack type is linked to its own detailed page with in-depth information, prevention strategies, and real-world examples.
          </p>

          {/* Search Bar */}
          <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-4">
              <label htmlFor="search" className="font-bold text-slate-700 dark:text-slate-300 min-w-fit">
                Search Web Attacks:
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search for specific web attacks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="space-y-4 mt-8">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              All Web & Application Attack Types:
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredAttacks.map((attack, index) => (
                <Link
                  key={index}
                  to={`/blog/attack/${attack.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
                  className={styles.link + " block p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"}
                >
                  {attack}
                  <span className="ml-2 text-xs text-blue-500">Explore →</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-4 mb-6">
            <Link
              to="/blog"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/blog/cyber-attack-categories"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              ← Back to Categories
            </Link>
            <Link
              to="/toolkit"
              className="text-blue-400 hover:text-blue-300 dark:text-slate-200 dark:hover:text-white underline"
            >
              Security Tools →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Popular Topics:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/what-is-cybersecurity" className={styles.link}>What is Cybersecurity?</Link></li>
                <li><Link to="/blog/what-is-firewall" className={styles.link}>What is a Firewall?</Link></li>
                <li><Link to="/blog/what-is-vpn" className={styles.link}>What is a VPN?</Link></li>
                <li><Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI Driven Cyber Attacks</Link></li>
                <li><Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Cyber Attack Types:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/common-cyber-attacks" className={styles.link}>Common Cyber Attacks</Link></li>
                <li><Link to="/blog/most-popular-cyber-attacks" className={styles.link}>Most Popular Cyber Attacks</Link></li>
                <li><Link to="/blog/top-10-cyber-attacks" className={styles.link}>Top 10 Cyber Attacks</Link></li>
                <li><Link to="/blog/worst-cyber-attacks" className={styles.link}>Worst Cyber Attacks</Link></li>
                <li><Link to="/blog/every-type-of-cyber-attack" className={styles.link}>Every Type of Cyber Attack</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Related Cyber Attacks:
              </h3>
              <div className="space-y-2">
                <div><Link to="/event/log4j-log4shell-2021" className={styles.link}>Log4j Log4Shell (2021)</Link></div>
                <div><Link to="/event/heartbleed" className={styles.link}>Heartbleed (2014)</Link></div>
                <div><Link to="/event/stuxnet" className={styles.link}>Stuxnet (2010)</Link></div>
                <div><Link to="/event/operation-aurora" className={styles.link}>Operation Aurora (2009)</Link></div>
                <div><Link to="/event/colonial-pipeline-ransomware" className={styles.link}>Colonial Pipeline Attack (2021)</Link></div>
                <div><Link to="/event/equifax-data-breach-2017" className={styles.link}>Equifax Data Breach (2017)</Link></div>
                <div><Link to="/event/bangladesh-bank-heist" className={styles.link}>Bangladesh Bank Heist (2016)</Link></div>
                <div><Link to="/event/anthem-breach" className={styles.link}>Anthem Breach (2015)</Link></div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4">
              <Link to="/sitemap.xml" className={styles.link}>Sitemap</Link>
              <Link to="/privacy-policy" className={styles.link}>Privacy Policy</Link>
              <Link to="/terms-of-service" className={styles.link}>Terms of Service</Link>
              <Link to="/contact" className={styles.link}>Contact Us</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default WebApplicationAttacks;